import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, Edit, Trash2, Megaphone, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export default function AnnouncementsTab({ onUpdate }) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingAnnouncement, setDeletingAnnouncement] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    type: "notice",
    is_active: true,
    start_date: "",
    end_date: "",
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API}/api/announcements`, {
        headers: getAuthHeader(),
      });
      setAnnouncements(response.data);
    } catch (error) {
      toast.error("Failed to load announcements");
    }
  };

  const openCreateDialog = () => {
    setEditingAnnouncement(null);
    setFormData({
      title: "",
      content: "",
      type: "notice",
      is_active: true,
      start_date: "",
      end_date: "",
    });
    setShowDialog(true);
  };

  const openEditDialog = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      is_active: announcement.is_active,
      start_date: announcement.start_date ? announcement.start_date.split("T")[0] : "",
      end_date: announcement.end_date ? announcement.end_date.split("T")[0] : "",
    });
    setShowDialog(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.content) {
      toast.error("Please fill in title and content");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        start_date: formData.start_date ? new Date(formData.start_date).toISOString() : null,
        end_date: formData.end_date ? new Date(formData.end_date).toISOString() : null,
      };

      if (editingAnnouncement) {
        await axios.put(
          `${API}/api/announcements/${editingAnnouncement.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success("Announcement updated");
      } else {
        await axios.post(`${API}/api/announcements`, payload, {
          headers: getAuthHeader(),
        });
        toast.success("Announcement created");
      }

      setShowDialog(false);
      fetchAnnouncements();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to save announcement");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteDialog = (announcement) => {
    setDeletingAnnouncement(announcement);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingAnnouncement) return;

    try {
      await axios.delete(`${API}/api/announcements/${deletingAnnouncement.id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Announcement deleted");
      setShowDeleteDialog(false);
      setDeletingAnnouncement(null);
      fetchAnnouncements();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to delete announcement");
    }
  };

  const toggleActive = async (announcement) => {
    try {
      await axios.put(
        `${API}/api/announcements/${announcement.id}`,
        { is_active: !announcement.is_active },
        { headers: getAuthHeader() }
      );
      toast.success(`Announcement ${!announcement.is_active ? "activated" : "deactivated"}`);
      fetchAnnouncements();
      if (onUpdate) onUpdate();
    } catch (error) {
      toast.error("Failed to update announcement");
    }
  };

  const isActive = (announcement) => {
    if (!announcement.is_active) return false;
    
    const now = new Date();
    if (announcement.start_date && new Date(announcement.start_date) > now) return false;
    if (announcement.end_date && new Date(announcement.end_date) < now) return false;
    
    return true;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl font-bold text-brand-navy tracking-tight">Announcements & Notices</h2>
          <p className="text-gray-500 text-sm">
            Manage notifications and advertisements visible to all users
          </p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          New Announcement
        </Button>
      </div>

      {announcements.length === 0 ? (
        <Card className="p-8 text-center">
          <Megaphone className="h-12 w-12 mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No announcements yet. Create one to get started!</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {announcements.map((announcement) => (
            <Card key={announcement.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    {announcement.type === "notice" ? (
                      <AlertCircle className="h-5 w-5 text-blue-600" />
                    ) : (
                      <Megaphone className="h-5 w-5 text-purple-600" />
                    )}
                    <h3 className="font-semibold text-lg">{announcement.title}</h3>
                    {isActive(announcement) ? (
                      <Badge className="bg-green-100 text-green-800">Active</Badge>
                    ) : (
                      <Badge variant="outline">Inactive</Badge>
                    )}
                    <Badge variant="outline">
                      {announcement.type === "notice" ? "Notice" : "Advertisement"}
                    </Badge>
                  </div>
                  <p className="text-gray-700 mb-2">{announcement.content}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>By: {announcement.created_by_name}</span>
                    {announcement.start_date && (
                      <span>Start: {new Date(announcement.start_date).toLocaleDateString()}</span>
                    )}
                    {announcement.end_date && (
                      <span>End: {new Date(announcement.end_date).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant={announcement.is_active ? "outline" : "default"}
                    onClick={() => toggleActive(announcement)}
                  >
                    {announcement.is_active ? "Deactivate" : "Activate"}
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => openEditDialog(announcement)}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => openDeleteDialog(announcement)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingAnnouncement ? "Edit Announcement" : "Create New Announcement"}
            </DialogTitle>
            <DialogDescription>
              Create notifications for delivery changes or promotional advertisements
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => setFormData({ ...formData, type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="notice">🔔 Notice (Important notifications)</SelectItem>
                  <SelectItem value="advertisement">📣 Advertisement (Promotions)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Delivery Schedule Change"
              />
            </div>

            <div>
              <Label>Content *</Label>
              <Textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="e.g., Due to heavy rain, we will deliver orders one day earlier."
                rows={4}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>End Date (Optional)</Label>
                <Input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_active"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              <Label htmlFor="is_active" className="cursor-pointer">
                Activate immediately
              </Label>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Announcement</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{deletingAnnouncement?.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
